/**
 * Row header renderer with add/remove controls for Handsontable
 */
import type Handsontable from 'handsontable';
import addBtn from '@/assets/images/add-circle-line.svg?raw';
import removeBtn from '@/assets/images/indeterminate-circle-line.svg?raw';
import styles from './HoT.module.scss';

/**
 * Row header renderer that adds add/remove row controls
 *
 * Used with afterGetRowHeader hook to render custom row headers
 * with interactive buttons for adding and removing rows.
 *
 * @param this - Handsontable Core instance (bound by HOT)
 * @param row - Row index
 * @param TH - Table header cell element
 */
export const rowHeaderWithControlsRenderer = function (
  this: Handsontable.Core,
  row: number,
  TH: HTMLTableHeaderCellElement
): void {
  TH.innerHTML = '';
  TH.setAttribute('data-test-element', 'row-header-with-controls');
  TH.setAttribute('data-test-key', `row-${row}`);

  const container = document.createElement('div');
  container.classList.add(styles.Container);

  if (this.countRows() === row + 1) {
    // Last row - show add button
    const atRowLimit = this.countRows() >= (this.getSettings().maxRows ?? Infinity);

    const svgContainer = document.createElement('span');
    svgContainer.setAttribute('data-test-element', 'add-row-button');
    svgContainer.classList.add(styles.Add);
    svgContainer.innerHTML = addBtn;

    svgContainer.onmousedown = (event: MouseEvent) => {
      if (!(this.getActiveEditor()?.isOpened() ?? false)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (atRowLimit) {
      svgContainer.classList.add(styles.Disabled);
      svgContainer.setAttribute('data-tooltip-id', 'global-tooltip-id');
      svgContainer.setAttribute('data-tooltip-content', 'The maximum number of rows has been reached');
    } else {
      svgContainer.onclick = () => {
        if (!(this.getActiveEditor()?.isOpened() ?? false)) {
          const rowsBeforeInsert = this.countRows();
          this.alter('insert_row_above', row, 1, 'add-row.click');

          // `alter` is a no-op once `maxRows` is reached. The nudge below is what tells listeners
          // a row arrived, so sending it anyway would report a change for the untouched blank row
          // and have it validated as if the user had filled it in.
          if (this.countRows() > rowsBeforeInsert) {
            this.setDataAtCell(row, 0, this.getDataAtCell(row, 0) ?? null, 'add-row.click');
          }
        }
      };
    }

    container.appendChild(svgContainer);
  } else {
    // Other rows - show row number and remove button
    const rowNumber = document.createElement('span');
    rowNumber.classList.add(styles.Number);
    rowNumber.innerText = `${row + 1}`;

    const svgContainer = document.createElement('span');
    svgContainer.setAttribute('data-test-element', 'remove-row-button');
    svgContainer.classList.add(styles.Remove);
    svgContainer.innerHTML = removeBtn;

    svgContainer.onmousedown = (event: MouseEvent) => {
      if (!(this.getActiveEditor()?.isOpened() ?? false)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    svgContainer.onclick = () => {
      if (!(this.getActiveEditor()?.isOpened() ?? false)) {
        this.alter('remove_row', row, 1, 'remove-row.click');
      }
    };

    container.appendChild(rowNumber);
    container.appendChild(svgContainer);
  }

  TH.appendChild(container);
  TH.classList.add(styles.RowHeaderWithControls);
};
